import { IonIcon } from '@ionic/react';
import {
  businessOutline,
  chevronDownOutline,
  chevronUpOutline,
  locationOutline,
  medicalOutline,
} from 'ionicons/icons';
import React, { useState } from 'react';

import ChatbotCall from '../../../../components/Shared/Call/ChatbotCall';
import './botSiteCard.scss';

interface Site {
  site_id: number;
  site_name: string;
  site_location: string;
  full_phone_number: string;
}

interface BotSiteCardProps {
  sites: Site[];
  maxVisibleSites?: number;
}

const BotSiteCard: React.FC<BotSiteCardProps> = ({ sites, maxVisibleSites = 3 }) => {
  const [expandedSiteIds, setExpandedSiteIds] = useState<number[]>([]);
  const [showAllSites, setShowAllSites] = useState(false);

  const toggleSiteDetails = (id: number) => {
    setExpandedSiteIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const formatLocation = (location: string) => {
    const parts = location?.split('|');
    if (parts?.length >= 3) {
      return `${parts[2]}, ${parts[1]}, ${parts[0]}`;
    }
    return location;
  };

  const formatPhoneNumber = (phone: string) => {
    return phone?.startsWith('+') ? phone : `+${phone}`;
  };

  if (!sites?.length) return null;

  const visibleSites = showAllSites ? sites : sites.slice(0, maxVisibleSites);
  const remainingCount = sites.length - maxVisibleSites;

  return (
    <div className="bot-site-card">
      {/* Header */}
      <div className="card-header">
        <div className="header-left">
          <div className="header-icon">
            <IonIcon icon={businessOutline} />
          </div>
          <span className="header-title">Sites</span>
        </div>
        <div className="site-badge">
          <span>{sites?.length}</span>
        </div>
      </div>

      {/* Sites List */}
      <div className="site-list">
        {visibleSites.map(site => {
          const isExpanded = expandedSiteIds.includes(site.site_id);
          const formattedLocation = formatLocation(site.site_location);
          const callParams = {
            callTo: 'sites',
            contactNumber: site.full_phone_number || '',
            name: site.site_name || '',
          };

          return (
            <div key={site.site_id} className={`site-card ${isExpanded ? 'expanded' : ''}`}>
              {/* Site Header */}
              <div className="site-header" onClick={() => toggleSiteDetails(site.site_id)}>
                <div className="site-icon">
                  <IonIcon icon={medicalOutline} />
                </div>
                <div className="site-info">
                  <div className="site-name" title={site.site_name}>{site.site_name}</div>
                  <div className="site-id">Site ID: {site.site_id}</div>
                </div>
                <IonIcon
                  icon={isExpanded ? chevronUpOutline : chevronDownOutline}
                  className={`accordion-icon ${isExpanded ? 'rotated' : ''}`}
                />
              </div>

              {/* Site Details */}
              {isExpanded && (
                <div className="site-details">
                  <div className="detail-row">
                    <div className="detail-icon">
                      <IonIcon icon={locationOutline} color="medium" />
                    </div>
                    <span className="detail-text" title={formattedLocation}>{formattedLocation}</span>
                  </div>
                  <div className="detail-row">
                    <div className="detail-icon">
                      <ChatbotCall
                        selectedDoctorGuid=""
                        dataFromParent={callParams}
                        isAssignedNumber={true}
                      />
                    </div>
                    <span className="detail-text">{formatPhoneNumber(site.full_phone_number)}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!showAllSites && remainingCount > 0 && (
          <div
            className="show-more"
            onClick={() => setShowAllSites(true)}
          >
            +{remainingCount} more
          </div>
        )}

        {showAllSites && sites.length > maxVisibleSites && (
          <div
            className="show-more"
            onClick={() => setShowAllSites(false)}
          >
            Show less
          </div>
        )}

      </div>
    </div>
  );
};

export default BotSiteCard;